import React from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/site/SiteLayout";
import Seo, { breadcrumbLd, faqLd, ogUrl } from "@/components/site/Seo";
import LeadForm from "@/components/site/LeadForm";
import { HowPaid, HOW_PAID_FAQ } from "@/components/site/conversion";

const BASE = "https://thesourcingatlas.com";

import { SERVICE_DATA, ProcessSteps } from "@/components/site/PublicPresentation";
import { ArrowUpRight } from "lucide-react";

export default function Services() {
  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Sourcing, verification, inspection and shipping",
    provider: { "@type": "Organization", name: "The Sourcing Atlas", url: BASE },
    areaServed: ["IN", "CN", "VN", "TH", "Worldwide"],
  };
  return (
    <SiteLayout>
      <Seo
        title="Sourcing Services — Verification, QC, Shipping & China+1 | The Sourcing Atlas"
        description="Everything needed to source, verify and ship from Asia — supplier discovery, factory verification, pre-shipment AQL inspection, freight, private label and China+1 supply-chain diversification."
        path="/services"
        ogImage={ogUrl("Sourcing, Verification, QC, Shipping & China+1", "What We Do")}
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]), serviceLd, faqLd([HOW_PAID_FAQ])]}
      />
      <div className="crumb"><Link to="/">Home</Link> › Services</div>
      <div className="pband">
        <div className="pband-in">
          <span className="tierpill tn">WHAT WE DO</span>
          <h1 data-testid="services-title">From sourcing intelligence<br />to hands-on execution.</h1>
          <p className="lede">Everything needed to source, verify and ship from Asia — one partner, many services, on a transparent disclosed commission.</p>
        </div>
      </div>

      <section>
        <div className="wrap">
          <div className="at-service-grid">{SERVICE_DATA.map(({ id, icon: Icon, title, desc, audience }) => <article id={id === "china-plus-one" ? "china-plus-one-service" : id} className="at-service-card" data-testid={`service-${id}`} key={id} data-reveal><Icon size={27} strokeWidth={1.3} /><h3>{title}</h3><p>{desc}</p><div className="at-service-audience">FOR {audience.toUpperCase()}</div><Link className="at-text-link" to={id === "tours" ? "/tours" : id === "verification" ? "/contact?intent=verification" : "/contact"} data-testid={`service-cta-${id}`}>{id === "tours" ? "Explore buying visits" : "Discuss your brief"}<ArrowUpRight size={15} /></Link></article>)}</div>
        </div>
      </section>

      <section style={{ background: "var(--wash)" }} id="china-plus-one">
        <div className="wrap" style={{ maxWidth: 900 }}>
          <div className="k2">CHINA + 1 STRATEGY</div>
          <h2>What is China+1 sourcing?</h2>
          <p className="sub2">
            China+1 sourcing is a dual-sourcing strategy: you keep your existing Chinese supplier and
            add a verified alternate in India, Vietnam or Thailand, so tariffs, freight shocks or
            capacity issues in one country do not break your supply chain. We benchmark your product
            across all four countries — landed-cost comparison, duty impact and a verified alternate
            supplier — before you are forced to find one in a hurry.
          </p>
          <div style={{ textAlign: "center" }}>
            <Link className="btn btn-red" to="/contact">Get a +1 Assessment</Link>
          </div>
        </div>
      </section>

      <section>
        <div className="wrap" style={{ maxWidth: 900 }}>
          <HowPaid />
        </div>
      </section>

      <ProcessSteps />
      <section id="requirement">
        <div className="wrap">
          <div className="reqband">
            <div className="reqcopy">
              <div className="k2" style={{ textAlign: "left", color: "#E8C67A" }}>START A BRIEF</div>
              <h2>Tell us what you need sourced</h2>
              <p>Attach a drawing, photo or spec sheet — our team responds with feasibility, a country recommendation and an indicative cost band within one business day.</p>
            </div>
            <div className="reqcard"><LeadForm source="enquiry" withFile /></div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
